import { useState, useCallback } from 'react';
import { Bootpay } from '@bootpay/client-js';
import axios from 'utils/axiosInstance';
import getRootData from 'utils/getRootData';
import { UserInfo } from 'types/user_info';

const VITE_BOOTPAY_JAVASCRIPT_KEY = import.meta.env.VITE_BOOTPAY_JAVASCRIPT_KEY;

interface RootData {
  userInfo: UserInfo;
}

interface IdentityVerificationStatus {
  is_verified: boolean;
  verification?: {
    authenticated_at: string;
    status: string;
    status_locale: string;
    pg: string;
    payment_method: string;
  };
}

interface IdentityVerificationState {
  isVerified: boolean;
  isChecking: boolean;
  isVerifying: boolean;
  error: string | null;
  status: IdentityVerificationStatus | null;
}

export const useIdentityVerification = (plan_key?: string, selectedMemberId?: number) => {
  const { userInfo } = getRootData<RootData>() ?? { userInfo: null };

  const [state, setState] = useState<IdentityVerificationState>({
    isVerified: false,
    isChecking: false,
    isVerifying: false,
    error: null,
    status: null,
  });

  // 본인인증 상태 확인
  const checkVerificationStatus = useCallback(async () => {
    setState(prev => ({ ...prev, isChecking: true, error: null }));

    try {
      const response = await axios.get('/payment/check_identity_verification');
      const data = response.data;

      if (data.success) {
        setState(prev => ({
          ...prev,
          isVerified: data.is_verified,
          status: data,
          isChecking: false,
          error: null, // 성공시 에러 메시지 초기화
        }));
        return data.is_verified;
      } else {
        // API 호출은 성공했지만 본인인증 상태가 false인 경우
        // 에러 메시지를 표시하지 않고 단순히 미인증 상태로 처리
        setState(prev => ({
          ...prev,
          isVerified: false,
          status: data,
          isChecking: false,
          error: null, // 본인인증 안 한 상태는 에러가 아님
        }));
        return false;
      }
    } catch (error: any) {
      console.error('Identity verification status check error:', error);
      // 실제 네트워크 오류나 서버 오류인 경우에만 에러 메시지 표시
      if (error.response?.status >= 500) {
        setState(prev => ({
          ...prev,
          error: '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
          isChecking: false,
        }));
      } else {
        // 401, 404 등은 단순히 미인증 상태로 처리
        setState(prev => ({
          ...prev,
          isVerified: false,
          isChecking: false,
          error: null,
        }));
      }
      return false;
    }
  }, []);

  // 본인인증 시작
  const startVerification = useCallback(async () => {
    if (!userInfo) {
      setState(prev => ({ ...prev, error: '로그인이 필요합니다.' }));
      return false;
    }

    setState(prev => ({ ...prev, isVerifying: true, error: null }));

    try {
      // 1단계: 본인인증 요청 ID 생성
      const prepareResponse = await axios.post('/payment/request_identity_verification');
      if (!prepareResponse.data.success) {
        throw new Error(prepareResponse.data.message || '본인인증 요청 준비에 실패했습니다.');
      }

      const { authentication_id } = prepareResponse.data;

      // 2단계: Bootpay 본인인증 실행
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const response = await Bootpay.requestAuthentication({
        application_id: VITE_BOOTPAY_JAVASCRIPT_KEY,
        pg: 'kcp',
        order_name: '본인인증',
        authentication_id: authentication_id,
        user: {
          id: userInfo.userId?.toString(),
          username: userInfo.name,
          phone: userInfo.phone || ''
        },
        show_agree_window: 1,
        redirect_url: baseUrl + `/payment/identity_verification_callback?plan_key=${plan_key || ''}`
      });

      if (response.event === 'done') {
        // 3단계: 본인인증 결과 처리
        const completeResponse = await axios.post('/payment/complete_identity_verification', {
          receipt_id: response.receipt_id,
          authentication_id: authentication_id,
          selected_member_id: selectedMemberId // MemberCheck에서 선택된 member ID
        });

        if (completeResponse.data.success) {
          setState(prev => ({
            ...prev,
            isVerified: true,
            isVerifying: false,
          }));

          // 상태 새로고침
          await checkVerificationStatus();
          return true;
        } else {
          throw new Error(completeResponse.data.message || '본인인증 처리에 실패했습니다.');
        }
      } else {
        setState(prev => ({ ...prev, isVerifying: false }));
        return false;
      }
    } catch (e: any) {
      console.error('Identity verification error:', e);

      if (e.event === 'cancel') {
        // 본인인증 취소는 에러가 아니므로 에러 메시지 표시하지 않음
        console.log('Identity verification cancelled by user');
        setState(prev => ({
          ...prev,
          error: null,
          isVerifying: false,
        }));
        return false;
      } else if (e.event === 'error') {
        setState(prev => ({
          ...prev,
          error: '본인인증 중 오류가 발생했습니다.',
          isVerifying: false,
        }));
        return false;
      } else if (e.message) {
        setState(prev => ({
          ...prev,
          error: e.message,
          isVerifying: false,
        }));
        return false;
      }

      setState(prev => ({
        ...prev,
        error: '본인인증 중 오류가 발생했습니다.',
        isVerifying: false,
      }));
      return false;
    }
  }, [userInfo, checkVerificationStatus]);

  return {
    ...state,
    checkVerificationStatus,
    startVerification,
  };
};